import { formatDateTime } from '@pickleballinc/lib/format-dates';

import { WORLD_PICKLEBALL_RANKINGS } from '@/modules/rankings/constants';
import { PLAYER_PROFILE_DIVISION_TYPES, PprDivisionType } from '@/modules/rankings/ppr/constants';
import {
  GameScore,
  PlayerInfo,
  PlayerStatsPeriod,
  PlayerStatsResponse,
  StatItem,
  Team,
  TournamentEventDetails,
  TournamentEventMatch,
  TournamentEventMatchSummary,
} from '@/modules/rankings/ppr/types';

export function getAgeFromDob(dob: Date) {
  const diff = Date.now() - new Date(dob).getTime();
  return Math.abs(new Date(diff).getUTCFullYear() - 1970);
}

export const getPlays = (plays: string) => {
  if (plays === 'RT') {
    return 'Right-handed';
  } else if (plays === 'LT') {
    return 'Left-handed';
  }
  return '-';
};

export const getGender = (gender: string) => {
  if (gender === 'M') {
    return 'Male';
  } else if (gender === 'F') {
    return 'Female';
  }
  return '-';
};

export const convertFeetAndInchesToCentimeters = (feet: number, inches: number): number => {
  const totalInches = Number(feet * 12) + Number(inches);
  const centimeters = Number(totalInches * 2.54);
  return Math.floor(centimeters);
};

export function getSeedTopStats(
  activeTab: string,
  seedStats: PlayerStatsPeriod,
  divisionType: PprDivisionType | null
): StatItem[] {
  return [
    { label: `${activeTab} Rank`, value: String(seedStats.ppaRank) },
    {
      label: `${activeTab} Points`,
      value: String(seedStats.ppaPoints),
    },
    {
      label: 'Tournaments',
      value: String(seedStats.tournamentsNumber),
      subValue: `out of ${getDivisionTournamentsPlayed(divisionType, seedStats)}`,
    },
  ];
}

export function getGameStats(seedStats: PlayerStatsPeriod): StatItem[] {
  const { gameStats } = seedStats;
  // TODO: BE does not support Avg Partners Ranking / Avg Opponents Ranking yet — hidden until available
  // const isSingles = activeTab === 'Current Singles Seed';

  return [
    { label: 'Points Earned', value: String(gameStats.pointsEarned) },
    { label: 'Points Against', value: String(gameStats.pointsAgainst) },
    { label: 'Point Differential', value: String(gameStats.difference) },
    // ...(isSingles
    //   ? []
    //   : [
    //       {
    //         label: 'Avg Partners Ranking',
    //         value: gameStats.avgPartnerRating == null ? '—' : gameStats.avgPartnerRating.toFixed(3),
    //       },
    //     ]),
    // {
    //   label: 'Avg Opponents Ranking',
    //   value: gameStats.avgOpponentsRating == null ? '—' : gameStats.avgOpponentsRating.toFixed(3),
    // },
  ];
}

export function formatPlayerStatsResponse(data: PlayerStatsResponse) {
  const formatPeriod = (period: PlayerStatsPeriod) => ({
    ...period,
    startToEndDate: `${formatDateTime(period.startDate)} - ${formatDateTime(period.endDate)}`,
  });

  return {
    seedStats: formatPeriod(data.seedStats),
    raceStats: formatPeriod(data.raceStats),
  };
}

export function mapTournamentEventMatch(match: TournamentEventMatch): TournamentEventMatchSummary {
  return {
    eventUuid: match.eventUuid,
    matchUuid: match.matchUuid,
    matchNumber: match.matchNumber,
    roundTitle: match.roundTitle,
    matchStartDate: match.matchStartDate,
    teamOne: {
      playerOne: match.teamOnePlayerOneName
        ? {
            firstName: match.teamOnePlayerOneName.split(' ')[0] || '',
            lastName: match.teamOnePlayerOneName.split(' ').slice(1).join(' '),
            imageUrl: match.teamOnePlayerOneImageUrl || null,
          }
        : null,
      playerTwo: match.teamOnePlayerTwoName
        ? {
            firstName: match.teamOnePlayerTwoName.split(' ')[0] || '',
            lastName: match.teamOnePlayerTwoName.split(' ').slice(1).join(' '),
            imageUrl: match.teamOnePlayerTwoImageUrl || null,
          }
        : null,
    },
    teamTwo: {
      playerOne: match.teamTwoPlayerOneName
        ? {
            firstName: match.teamTwoPlayerOneName.split(' ')[0] || '',
            lastName: match.teamTwoPlayerOneName.split(' ').slice(1).join(' '),
            imageUrl: match.teamTwoPlayerOneImageUrl || null,
          }
        : null,
      playerTwo: match.teamTwoPlayerTwoName
        ? {
            firstName: match.teamTwoPlayerTwoName.split(' ')[0] || '',
            lastName: match.teamTwoPlayerTwoName.split(' ').slice(1).join(' '),
            imageUrl: match.teamTwoPlayerTwoImageUrl || null,
          }
        : null,
    },
    streamingServices: match.streamingServices,
  };
}

export const isPlayerInTeam = (team: Team, input: string): boolean => {
  if (!team || !input) return false;
  const playerOneFullName =
    `${team.playerOne?.firstName ?? ''} ${team.playerOne?.lastName ?? ''}`.toLowerCase();
  const playerTwoFullName =
    `${team.playerTwo?.firstName ?? ''} ${team.playerTwo?.lastName ?? ''}`.toLowerCase();
  return playerOneFullName.includes(input) || playerTwoFullName.includes(input);
};

export const reorderTeamPlayers = (team: Team, playerName: string): Team => {
  if (!team || !playerName || !team.playerTwo) return team;
  const input = playerName.toLowerCase().trim();
  const playerTwoFullName =
    `${team.playerTwo?.firstName ?? ''} ${team.playerTwo?.lastName ?? ''}`.toLowerCase();
  if (playerTwoFullName.includes(input)) {
    return {
      ...team,
      playerOne: team.playerTwo,
      playerTwo: team.playerOne,
    };
  }
  return team;
};

export const abbreviateFullName = (fullName: string) => {
  const trimmed = fullName.trim();
  const spaceIndex = trimmed.indexOf(' ');
  if (spaceIndex === -1) return trimmed;
  return `${trimmed.charAt(0)}. ${trimmed.slice(spaceIndex + 1)}`;
};

export const getMedalists = (event: TournamentEventDetails, playerName: string) => {
  const all = [
    { label: 'Gold', name: event.goldMedalist },
    { label: 'Silver', name: event.silverMedalist },
    { label: 'Bronze', name: event.bronzeMedalist },
    { label: 'Fourth', name: event.fourthPlace },
  ].filter((m) => !!m.name);

  const input = playerName.toLowerCase().trim();
  const playerIndex = all.findIndex((m) => m.name.toLowerCase().includes(input));

  if (playerIndex > 0) {
    const playerMedal = all.splice(playerIndex, 1)[0];
    if (playerMedal) {
      all.unshift(playerMedal);
    }
  }

  return all.map((m) => ({
    ...m,
    isPlayer: m.name.toLowerCase().includes(input),
    playerName,
  }));
};

export const getGameScores = (match: TournamentEventMatch, swapped: boolean): GameScore[] => {
  const rawTeamOneScores = [
    match.teamOneGameOneScore,
    match.teamOneGameTwoScore,
    match.teamOneGameThreeScore,
    match.teamOneGameFourScore,
    match.teamOneGameFiveScore,
  ];

  const rawTeamTwoScores = [
    match.teamTwoGameOneScore,
    match.teamTwoGameTwoScore,
    match.teamTwoGameThreeScore,
    match.teamTwoGameFourScore,
    match.teamTwoGameFiveScore,
  ];

  const teamOneScores = swapped ? rawTeamTwoScores : rawTeamOneScores;
  const teamTwoScores = swapped ? rawTeamOneScores : rawTeamTwoScores;

  const allGames = Array.from({ length: 5 }, (_, i) => ({
    label: `G${i + 1}`,
    teamOneScore: teamOneScores[i] ?? 0,
    teamTwoScore: teamTwoScores[i] ?? 0,
  }));

  const playedGames = allGames.filter((g) => !(g.teamOneScore === 0 && g.teamTwoScore === 0));

  return playedGames.length > 0 ? playedGames : allGames.slice(0, 1);
};

export const getFullName = (player: PlayerInfo) =>
  `${player.firstName ?? ''} ${player.lastName ?? ''}`;

export const getShortName = (player: PlayerInfo) =>
  `${player.firstName.charAt(0)}. ${player.lastName}`;

export function getDivisionTabTitle(divisionType: string): string {
  const match = PLAYER_PROFILE_DIVISION_TYPES.find(
    (d) => d.femaleType === divisionType || d.maleType === divisionType
  );
  return match?.title ?? WORLD_PICKLEBALL_RANKINGS;
}

const DIVISION_LABEL = {
  [PprDivisionType.WomensSingles]: 'Singles',
  [PprDivisionType.MensSingles]: 'Singles',
  [PprDivisionType.Mixed]: 'Mixed',
  [PprDivisionType.WomensDoubles]: 'Doubles',
  [PprDivisionType.MensDoubles]: 'Doubles',
} as const satisfies Record<PprDivisionType, string>;

export function parsePprDivisionType(value: string | null | undefined): PprDivisionType | null {
  if (value === null || value === undefined) return null;
  const known = Object.values(PprDivisionType) as string[];
  return known.includes(value) ? (value as PprDivisionType) : null;
}

export function getDivisionLabel(type: PprDivisionType | null): string {
  return type === null ? WORLD_PICKLEBALL_RANKINGS : DIVISION_LABEL[type];
}

interface DivisionTournamentsPlayed {
  singles365TournamentsPlayed: number;
  doubles365TournamentsPlayed: number;
  mixedDoubles365TournamentsPlayed: number;
}

export function getDivisionTournamentsPlayed(
  type: PprDivisionType | null,
  stats: DivisionTournamentsPlayed
): number {
  switch (type) {
    case PprDivisionType.WomensSingles:
    case PprDivisionType.MensSingles:
      return stats.singles365TournamentsPlayed;
    case PprDivisionType.WomensDoubles:
    case PprDivisionType.MensDoubles:
      return stats.doubles365TournamentsPlayed;
    case PprDivisionType.Mixed:
      return stats.mixedDoubles365TournamentsPlayed;
    default:
      return 0;
  }
}

type ProWorldRankingShare = { multiplier: number; label: string };

const PRO_WORLD_RANKING_SHARE = {
  [PprDivisionType.WomensSingles]: { multiplier: 0.15, label: '15%' },
  [PprDivisionType.MensSingles]: { multiplier: 0.15, label: '15%' },
  [PprDivisionType.Mixed]: { multiplier: 0.35, label: '35%' },
  [PprDivisionType.WomensDoubles]: { multiplier: 0.5, label: '50%' },
  [PprDivisionType.MensDoubles]: { multiplier: 0.5, label: '50%' },
} as const satisfies Record<PprDivisionType, ProWorldRankingShare>;

export function getProWorldRankingShare(type: PprDivisionType | null): ProWorldRankingShare {
  return type === null ? { multiplier: 1, label: '100%' } : PRO_WORLD_RANKING_SHARE[type];
}
